import { useEffect, useRef, useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import type { SharedProps } from '@/frontend/types/inertia'
import AgentDisclosure from './AgentDisclosure'

interface Props {
  mobileMenuOpen: boolean
  onNavigate: () => void
  compact?: boolean
}

function contextualWorkerId(url: string): string | null {
  const [path, query] = url.split('?')
  const pathId = path.match(/^\/dashboard\/digital_workers\/([^/]+)/)?.[1]
  if (pathId && pathId !== 'new') return decodeURIComponent(pathId)

  return new URLSearchParams(query ?? '').get('digital_worker_id')
}

export default function AgentsSection({ mobileMenuOpen, onNavigate, compact = false }: Props) {
  const page = usePage<SharedProps>()
  const workers = page.props.navigableResources?.workers ?? []
  const canManageResources = page.props.permissions?.can_manage_resources ?? false
  const businessProcessesEnabled = page.props.featureFlags?.business_processes_enabled ?? false
  const activeWorkerId = contextualWorkerId(page.url)
  const [expandedWorkerId, setExpandedWorkerId] = useState<string | null>(activeWorkerId)
  const activeRowRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (activeWorkerId) setExpandedWorkerId(activeWorkerId)
  }, [activeWorkerId])

  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeWorkerId, mobileMenuOpen])

  return (
    <section
      data-testid="wfn-agents-section"
      className={`flex min-h-0 flex-col px-2.5 pb-2 pt-4 ${compact ? 'shrink-0' : 'flex-1'}`}
    >
      <div className="flex items-center px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <h2 className="min-w-0 flex-1">Agentes</h2>
        {canManageResources && (
          <Link
            href="/dashboard/digital_workers/new"
            onClick={onNavigate}
            aria-label="Novo agente"
            data-testid="wfn-new-agent"
            className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
          </Link>
        )}
      </div>

      <div className={`min-h-0 pt-1 ${compact ? '' : 'flex-1 overflow-y-auto'}`}>
        {workers.length === 0 ? (
          <p className="px-2 py-2 text-xs leading-relaxed text-muted-foreground">
            Nenhum colaborador digital ainda.
          </p>
        ) : (
          <nav aria-label="Agentes" className="space-y-px">
            {workers.map((worker) => {
              const active = worker.id === activeWorkerId

              return (
                <div key={worker.id} ref={active ? activeRowRef : undefined}>
                  <AgentDisclosure
                    worker={worker}
                    currentUrl={page.url}
                    active={active}
                    open={expandedWorkerId === worker.id}
                    businessProcessesEnabled={businessProcessesEnabled}
                    onOpenChange={(open) => setExpandedWorkerId(open ? worker.id : null)}
                    onNavigate={onNavigate}
                  />
                </div>
              )
            })}
          </nav>
        )}
      </div>
    </section>
  )
}
